import { useEffect, useRef } from 'react';
import { createChart } from 'lightweight-charts';
import { motion } from 'framer-motion';

const PortfolioPerformance = ({ portfolioData = [], performanceData = [], userData }) => {
  const chartContainerRef = useRef();
  const chartRef = useRef();

  useEffect(() => {
    if (!performanceData || performanceData.length === 0) {
      return;
    }

    try {
      const chartOptions = {
        layout: {
          background: { color: '#ffffff' },
          textColor: '#333',
          fontSize: 12,
        },
        grid: {
          vertLines: { visible: false },
          horzLines: { color: 'rgba(200, 200, 200, 0.2)' },
        },
        width: chartContainerRef.current.clientWidth,
        height: 200,
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          borderColor: 'rgba(200, 200, 200, 0.4)',
        },
        rightPriceScale: {
          borderColor: 'rgba(200, 200, 200, 0.4)',
        },
        crosshair: {
          vertLine: {
            color: 'rgba(79, 70, 229, 0.2)',
            width: 1,
            style: 1,
          },
          horzLine: {
            color: 'rgba(79, 70, 229, 0.2)',
            width: 1,
            style: 1,
          },
        },
      };

      chartRef.current = createChart(chartContainerRef.current, chartOptions);
      const areaSeries = chartRef.current.addAreaSeries({
        topColor: 'rgba(79, 70, 229, 0.3)',
        bottomColor: 'rgba(79, 70, 229, 0.0)',
        lineColor: 'rgba(79, 70, 229, 1)',
        lineWidth: 2,
      });

      const chartData = performanceData.map(item => ({
        time: item.month,
        value: item.value,
      }));

      areaSeries.setData(chartData);
      chartRef.current.timeScale().fitContent();

      const handleResize = () => {
        if (chartRef.current) {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
          });
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        if (chartRef.current) {
          chartRef.current.remove();
        }
      };
    } catch (error) {
      console.error('Chart error:', error);
    }
  }, [performanceData]);

  // Calculate performance metrics
  const calculateMetrics = () => {
    if (!performanceData || performanceData.length < 2) {
      return {
        totalReturn: 0,
        monthlyGrowth: 0,
        annualizedReturn: 0
      };
    }

    const latest = performanceData[performanceData.length - 1].value;
    const first = performanceData[0].value;
    const totalReturn = ((latest - first) / first) * 100;
    const monthlyGrowth = totalReturn / performanceData.length;
    const annualizedReturn = monthlyGrowth * 12;

    return {
      totalReturn: totalReturn.toFixed(2),
      monthlyGrowth: monthlyGrowth.toFixed(2),
      annualizedReturn: annualizedReturn.toFixed(2)
    };
  };

  const metrics = calculateMetrics();

  return (
    <motion.div 
      className="space-y-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Performance Metrics */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-gray-50 rounded-lg p-3">
          <h3 className="text-xs font-medium text-gray-600">Total Balance</h3>
          <p className="text-base font-bold mt-1">
            ${userData?.balance?.toLocaleString() || '0'}
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <h3 className="text-xs font-medium text-gray-600">Total Invested</h3>
          <p className="text-base font-bold mt-1">
            ${userData?.totalInvested?.toLocaleString() || '0'}
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <h3 className="text-xs font-medium text-gray-600">Total Profit</h3>
          <p className="text-base font-bold mt-1 text-green-600">
            ${userData?.totalProfit?.toLocaleString() || '0'}
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <h3 className="text-xs font-medium text-gray-600">Monthly Growth</h3>
          <p className={`text-base font-bold mt-1 ${
            parseFloat(metrics.monthlyGrowth) >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {metrics.monthlyGrowth}%
          </p>
        </div>
      </div>

      {/* Performance Chart */}
      <div className="bg-white rounded-lg p-3">
        <div ref={chartContainerRef} className="w-full" />
      </div>

      {/* Portfolio Distribution */}
      <div className="grid grid-cols-2 gap-3">
        {portfolioData?.map((item, index) => (
          <div key={index} className="bg-gray-50 rounded-lg p-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-medium">{item.name}</span>
              <span className="text-xs font-bold">{item.value}%</span>
            </div>
            <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-600"
                style={{ width: `${item.value}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default PortfolioPerformance; 